import { ReactNode, useState, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useViewMode, ViewMode, TableDensity } from '../contexts/ViewModeContext';
import { useQuery } from '@tanstack/react-query';
import { QrScannerButton } from './QrScanner';
import { QuickActionButton } from './QuickAction';
import { api } from '../lib/api';
import { 
  Home, Ticket, Package, MapPin, Tag, 
  LogOut, ChevronDown, Settings, AlertTriangle, Plus
} from 'lucide-react';

interface LayoutProps {
  children: ReactNode;
}

const allNavItems = [
  { path: '/', label: 'Dashboard', icon: Home, requiresTicketing: false },
  { path: '/pending-actions', label: 'In Sospeso', icon: AlertTriangle, requiresTicketing: false },
  { path: '/tickets', label: 'Ticket', icon: Ticket, requiresTicketing: true },
  { path: '/assets', label: 'Asset', icon: Package, requiresTicketing: false },
  { path: '/locations', label: 'Ubicazioni', icon: MapPin, requiresTicketing: false },
  { path: '/categories', label: 'Categorie', icon: Tag, requiresTicketing: false },
];

export default function Layout({ children }: LayoutProps) {
  const { user, modules } = useAuth();
  const location = useLocation();
  const { viewMode, setViewMode, tableDensity, setTableDensity, isMobile } = useViewMode();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Fetch pending actions count for current user
  const { data: pendingData } = useQuery({
    queryKey: ['pending-actions'],
    queryFn: () => api.getPendingActions(),
    refetchInterval: 60000 // Refresh every minute
  });

  // Count of user's own pending actions
  const ownPendingCount = pendingData?.stats?.own || 0;

  // Filter navigation items based on enabled modules
  const navItems = useMemo(() => {
    return allNavItems.filter(item => {
      if (item.requiresTicketing && !modules.ticketingEnabled) {
        return false;
      }
      return true;
    });
  }, [modules.ticketingEnabled]);

  // Determine which page we're on for FAB+ visibility
  const showFabPlus = useMemo(() => {
    const path = location.pathname;
    // Show FAB+ only on list pages that support adding new items
    if (path === '/assets') return '/assets/new';
    if (path === '/locations') return 'modal:location';
    if (path === '/categories') return 'modal:category';
    if (modules.ticketingEnabled && path === '/tickets') return '/tickets/new';
    return null;
  }, [location.pathname, modules.ticketingEnabled]);

  const handleLogout = async () => {
    try {
      await api.logout();
    } catch (e) {
      // Ignore errors, proceed to login anyway
    }
    window.location.href = '/login';
  };

  const currentPage = navItems.find(item => {
    if (item.path === '/') return location.pathname === '/';
    return location.pathname.startsWith(item.path);
  });

  return (
    <div className="min-h-screen bg-[#f4f5f7]">
      {/* Top Header - Both Mobile and Desktop */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="flex items-center justify-between h-12 px-4">
          {/* Logo & Brand */}
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary-600 rounded flex items-center justify-center">
              <span className="text-white text-sm">⛺</span>
            </div>
            <span className="font-semibold text-gray-900 hidden sm:block">ArgoCamp</span>
            <span className="text-gray-300 hidden sm:block">|</span>
            <span className="text-sm text-gray-600 hidden sm:block">{currentPage?.label}</span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                className={({ isActive }) =>
                  `nav-item ${isActive ? 'active' : ''} relative`
                }
              >
                <item.icon className="nav-item-icon" />
                {item.label}
                {/* Pending actions counter badge */}
                {item.path === '/pending-actions' && ownPendingCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-amber-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {ownPendingCount > 99 ? '99+' : ownPendingCount}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-2">
            {/* Settings button - desktop only */}
            <div className="relative hidden sm:block">
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="icon-btn"
                title="Impostazioni visualizzazione"
              >
                <Settings className="w-4 h-4" />
              </button>
              
              {showSettings && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowSettings(false)} />
                  <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-2">
                    <div className="px-3 py-2 border-b border-gray-100">
                      <p className="text-xs font-medium text-gray-500 uppercase">Densità tabelle</p>
                    </div>
                    <div className="p-2">
                      {(['dense', 'normal', 'relaxed'] as TableDensity[]).map((density) => (
                        <button
                          key={density}
                          onClick={() => { setTableDensity(density); setShowSettings(false); }}
                          className={`w-full text-left px-3 py-1.5 text-sm rounded ${
                            tableDensity === density ? 'bg-primary-50 text-primary-700' : 'hover:bg-gray-50'
                          }`}
                        >
                          {density === 'dense' ? 'Compatta' : density === 'normal' ? 'Normale' : 'Comoda'}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 px-1 py-1 rounded hover:bg-gray-100"
              >
                <div className="w-9 h-9 rounded-full bg-primary-600 flex items-center justify-center text-white text-xs font-medium">
                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                </div>
                <span className="text-sm font-medium text-gray-700 hidden sm:block">
                  {user?.firstName}
                </span>
                <ChevronDown className="w-3 h-3 text-gray-400 hidden sm:block" />
              </button>

              {showUserMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                  <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
                    {/* User info */}
                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                      <p className="font-medium text-sm text-gray-900">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                    </div>
                    
                    {/* Mobile-only settings */}
                    <div className="sm:hidden border-b border-gray-100">
                      <div className="px-4 py-2">
                        <p className="text-xs font-medium text-gray-500 uppercase mb-2">Densità tabelle</p>
                        <div className="flex gap-1">
                          {(['dense', 'normal', 'relaxed'] as TableDensity[]).map((density) => (
                            <button
                              key={density}
                              onClick={() => setTableDensity(density)}
                              className={`flex-1 px-2 py-1 text-xs rounded ${
                                tableDensity === density 
                                  ? 'bg-primary-100 text-primary-700' 
                                  : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {density === 'dense' ? 'Comp.' : density === 'normal' ? 'Norm.' : 'Larga'}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Logout */}
                    <div className="p-2">
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded"
                      >
                        <LogOut className="w-4 h-4" />
                        Esci
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pb-16 lg:pb-4">
        <div className="p-4 lg:p-6 max-w-[1800px] mx-auto">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 pb-safe">
        <div className="flex items-center justify-around h-14">
          {navItems.map((item) => {
            const isActive = item.path === '/' 
              ? location.pathname === '/' 
              : location.pathname.startsWith(item.path);
            
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className="relative flex flex-col items-center justify-center w-full h-full"
              >
                <div className="relative">
                  <item.icon 
                    className={`w-5 h-5 ${isActive ? 'text-primary-600' : 'text-gray-400'}`} 
                  />
                  {/* Pending actions counter badge */}
                  {item.path === '/pending-actions' && ownPendingCount > 0 && (
                    <span className="absolute -top-1.5 -right-2.5 min-w-[16px] h-[16px] px-1 bg-amber-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                      {ownPendingCount > 99 ? '99+' : ownPendingCount}
                    </span>
                  )}
                </div>
                <span className={`text-[10px] mt-0.5 ${
                  isActive ? 'text-primary-600 font-medium' : 'text-gray-500'
                }`}>
                  {item.label}
                </span>
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* Floating Action Buttons Stack - positioned above mobile nav bar */}
      <div className="fixed right-4 bottom-[4.5rem] lg:bottom-6 z-30 flex flex-col gap-3">
        {/* FAB + - Top - Only shown on specific pages */}
        {showFabPlus && !showFabPlus.startsWith('modal:') && (
          <NavLink
            to={showFabPlus}
            className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-700 hover:from-primary-600 hover:to-primary-800 text-white rounded-full shadow-lg shadow-primary-500/30 flex items-center justify-center transition-all hover:scale-105 active:scale-95"
            aria-label="Aggiungi"
          >
            <Plus className="w-6 h-6" />
          </NavLink>
        )}
        {/* FAB + for modals - dispatches custom event */}
        {showFabPlus && showFabPlus.startsWith('modal:') && (
          <button
            onClick={() => {
              const modalType = showFabPlus.replace('modal:', '');
              window.dispatchEvent(new CustomEvent('openAddModal', { detail: { type: modalType } }));
            }}
            className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-700 hover:from-primary-600 hover:to-primary-800 text-white rounded-full shadow-lg shadow-primary-500/30 flex items-center justify-center transition-all hover:scale-105 active:scale-95"
            aria-label="Aggiungi"
          >
            <Plus className="w-6 h-6" />
          </button>
        )}
        {/* Quick Action - Middle */}
        <QuickActionButton />
        {/* QR Scanner - Bottom */}
        <QrScannerButton />
      </div>
    </div>
  );
}

